import { useState, useEffect } from 'react';
import { Plus, Sparkles, Loader2, Play, Pause, Trash2, X, CheckCircle, Bot, Zap, Clock, Users } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import api from '@/lib/api';

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ job_id: '', goal_description: '', max_candidates: 20, schedule_hours: 24 });
  const [generatingGoal, setGeneratingGoal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const fetchAgents = async () => {
    try {
      const { data } = await api.get('/agents');
      setAgents(data);
    } catch (err) { logger.error(err); }
    finally { setLoading(false); }
  };

  const fetchJobs = async () => {
    try {
      const { data } = await api.get('/jobs');
      setJobs(data.filter(j => j.status === 'active'));
    } catch (err) { logger.error(err); }
  };

  useEffect(() => { fetchAgents(); fetchJobs(); }, []);

  const generateGoal = async () => {
    if (!form.job_id) { addToast('Select a job first', 'error'); return; }
    setGeneratingGoal(true);
    try {
      const { data } = await api.post('/agents/generate-goal', { job_id: form.job_id });
      setForm(prev => ({ ...prev, goal_description: data.goal }));
      addToast('Goal generated!', 'success');
    } catch (err) {
      addToast(err?.response?.data?.detail || 'Failed to generate goal', 'error');
    } finally { setGeneratingGoal(false); }
  };

  const createAgent = async (e) => {
    e.preventDefault();
    if (!form.job_id || !form.goal_description.trim()) { addToast('Job and goal are required', 'error'); return; }
    setSaving(true);
    try {
      await api.post('/agents', form);
      setShowModal(false);
      setForm({ job_id: '', goal_description: '', max_candidates: 20, schedule_hours: 24 });
      fetchAgents();
      addToast('Sourcing agent created!', 'success');
    } catch (err) { addToast(err?.response?.data?.detail || 'Failed to create agent', 'error'); }
    finally { setSaving(false); }
  };

  const toggleAgent = async (agentId, currentStatus) => {
    try {
      await api.patch(`/agents/${agentId}/toggle`);
      fetchAgents();
      addToast(currentStatus === 'active' ? 'Agent paused' : 'Agent activated', 'success');
    } catch { addToast('Failed to toggle agent', 'error'); }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm('Delete this sourcing agent?')) return;
    try {
      await api.delete(`/agents/${agentId}`);
      fetchAgents();
      addToast('Agent deleted', 'success');
    } catch { addToast('Failed to delete agent', 'error'); }
  };

  const getJobTitle = (jobId) => jobs.find(j => j.job_id === jobId)?.title || jobId;

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-blue-50/30 min-h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Sourcing Agents</h1>
          <p className="text-slate-500 text-sm mt-1">Automated AI agents that continuously source candidates</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-emerald-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
          <Plus className="w-4 h-4" /> New Agent
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
      ) : agents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
            <Bot className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No sourcing agents yet</h3>
          <p className="text-slate-400 text-sm max-w-sm mb-6">Create an agent to automatically find and source candidates for your open roles around the clock.</p>
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" /> Create First Agent
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {agents.map(agent => (
            <div key={agent.agent_id || agent._id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    agent.status === 'active' ? 'bg-emerald-50' : 'bg-slate-100'
                  }`}>
                    <Bot className={`w-5 h-5 ${agent.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{getJobTitle(agent.job_id)}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      agent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>{agent.status === 'active' ? '● Active' : '○ Paused'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleAgent(agent.agent_id || agent._id, agent.status)}
                    className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500">
                    {agent.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button onClick={() => deleteAgent(agent.agent_id || agent._id)}
                    className="p-2 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors text-slate-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-600 mb-3 line-clamp-2">{agent.goal_description}</p>
              <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{agent.max_candidates} max</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Every {agent.schedule_hours}h</span>
                {agent.candidates_found > 0 && (
                  <span className="flex items-center gap-1 text-emerald-600"><CheckCircle className="w-3 h-3" />{agent.candidates_found} found</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Agent Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg p-7" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">New Sourcing Agent</h2>
                  <p className="text-xs text-slate-400">AI-powered continuous sourcing</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={createAgent} className="space-y-4">
              {/* Job Select */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1.5">Job Role <span className="text-red-500">*</span></label>
                <select value={form.job_id} onChange={e => setForm(prev => ({ ...prev, job_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  required>
                  <option value="">Select a job role...</option>
                  {jobs.map(j => (
                    <option key={j.job_id} value={j.job_id}>{j.title}</option>
                  ))}
                </select>
              </div>

              {/* Goal Description + AI Generate */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-slate-700">Sourcing Goal <span className="text-red-500">*</span></label>
                  <button type="button" onClick={generateGoal} disabled={generatingGoal || !form.job_id}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                    {generatingGoal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {generatingGoal ? 'Generating...' : '✨ AI Generate'}
                  </button>
                </div>
                <textarea
                  value={form.goal_description}
                  onChange={e => setForm(prev => ({ ...prev, goal_description: e.target.value }))}
                  rows={4}
                  placeholder="Describe what kind of candidates to source, required skills, experience level..."
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none"
                  required
                />
              </div>

              {/* Settings Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1.5">Max Candidates</label>
                  <input type="number" min={5} max={100} value={form.max_candidates}
                    onChange={e => setForm(prev => ({ ...prev, max_candidates: parseInt(e.target.value) }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:border-blue-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1.5">Run Every (hours)</label>
                  <select value={form.schedule_hours} onChange={e => setForm(prev => ({ ...prev, schedule_hours: parseInt(e.target.value) }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 bg-white focus:border-blue-500 outline-none">
                    <option value={6}>6 hours</option>
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours</option>
                    <option value={48}>48 hours</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm font-medium hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={saving || !form.job_id || !form.goal_description.trim()}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-emerald-600 text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : <><Zap className="w-4 h-4" />Create Agent</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
